import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

/**
 * Pareja · «Cuenta común con dos nóminas»: quién cobra en la cuenta común,
 * de quién es cada nómina (pref sincronizada `cuenta-comun-nominas`, JSON
 * `{ "<id pagador>": "<id persona>" }`) y cuánto pone cada uno.
 */

export type IngresoCuenta = {
  payee: string | null;
  date: string;
  amount: IntegerAmount;
};

export type NominaCuenta = {
  payee: string;
  /** Lo que suele ingresar (mediana de sus 3 últimos meses, al euro). */
  habitual: IntegerAmount;
  /** Día del mes en que suele llegar. */
  dia: number;
  /** Lo que ya ha ingresado este mes. */
  cobrado: IntegerAmount;
};

/** Menos de 300 € al mes no es una nómina. */
const NOMINA_MINIMA = 30000;

function mediana(valores: readonly number[]): number {
  const orden = [...valores].sort((a, b) => a - b);
  const mitad = Math.floor(orden.length / 2);
  return orden.length % 2
    ? orden[mitad]
    : (orden[mitad - 1] + orden[mitad]) / 2;
}

/**
 * Nóminas que entran en la cuenta común: pagadores que ingresan 300 € o más
 * en al menos 3 de los 6 meses anteriores y alguno de los 2 últimos. De mayor
 * a menor.
 */
export function nominasDeLaCuenta(
  ingresos: readonly IngresoCuenta[],
  mes: string,
): NominaCuenta[] {
  const anteriores = Array.from({ length: 6 }, (_, i) =>
    monthUtils.subMonths(mes, 6 - i),
  );
  const porPagador = new Map<
    string,
    { meses: Map<string, number>; dias: number[]; cobrado: number }
  >();
  for (const f of ingresos) {
    if (!f.payee || f.amount <= 0) {
      continue;
    }
    const datos = porPagador.get(f.payee) ?? {
      meses: new Map<string, number>(),
      dias: [],
      cobrado: 0,
    };
    const mesFila = f.date.slice(0, 7);
    if (mesFila === mes) {
      datos.cobrado += f.amount;
    } else if (anteriores.includes(mesFila)) {
      datos.meses.set(mesFila, (datos.meses.get(mesFila) ?? 0) + f.amount);
      datos.dias.push(Number(f.date.slice(8, 10)));
    }
    porPagador.set(f.payee, datos);
  }

  const nominas: NominaCuenta[] = [];
  for (const [payee, datos] of porPagador) {
    const conNomina = anteriores.filter(
      m => (datos.meses.get(m) ?? 0) >= NOMINA_MINIMA,
    );
    const reciente = conNomina.some(m => anteriores.slice(-2).includes(m));
    if (conNomina.length < 3 || !reciente) {
      continue;
    }
    const ultimos = conNomina.slice(-3).map(m => datos.meses.get(m) ?? 0);
    const cuenta = new Map<number, number>();
    for (const d of datos.dias) {
      cuenta.set(d, (cuenta.get(d) ?? 0) + 1);
    }
    const dia = [...cuenta].sort((a, b) => b[1] - a[1] || b[0] - a[0])[0][0];
    nominas.push({
      payee,
      habitual: Math.round(mediana(ultimos) / 100) * 100,
      dia,
      cobrado: datos.cobrado,
    });
  }
  return nominas.sort((a, b) => b.habitual - a.habitual);
}

/** Lee `cuenta-comun-nominas`; ilegible → vacío. */
export function leerDuenosNominas(
  raw: string | undefined | null,
): Record<string, string> {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const resultado: Record<string, string> = {};
    for (const [payee, persona] of Object.entries(parsed)) {
      if (typeof persona === 'string') {
        resultado[payee] = persona;
      }
    }
    return resultado;
  } catch {
    return {};
  }
}

export function ponerDuenoNomina(
  raw: string | undefined | null,
  payee: string,
  persona: string,
): string {
  return JSON.stringify({ ...leerDuenosNominas(raw), [payee]: persona });
}

export type Bodega = {
  /** Lo que hay para el mes siguiente (lo que llega). */
  hay: IntegerAmount;
  /** La raya: lo asignado (o previsto) para el mes siguiente. */
  necesita: IntegerAmount;
  /** Positivo: sobra; negativo: falta. */
  diferencia: IntegerAmount;
  /** Lo que pone cada nómina este mes y su parte (0-100) del total cobrado. */
  partes: { payee: string; cobrado: IntegerAmount; porcentaje: number }[];
  /** Nóminas que aún no han llegado este mes. */
  esperando: string[];
};

/**
 * La bodega: se llena con lo que llega al mes siguiente (saldo − lo que
 * queda por pagar) y marca la raya de lo asignado. Cada nómina es una capa.
 */
export function calcularBodega({
  nominas,
  llega,
  necesita,
}: {
  nominas: readonly NominaCuenta[];
  llega: IntegerAmount;
  necesita: IntegerAmount;
}): Bodega {
  const hay = Math.max(0, llega);
  const cobradoTotal = nominas.reduce((s, n) => s + n.cobrado, 0);
  const partes = nominas.map(n => ({
    payee: n.payee,
    cobrado: n.cobrado,
    porcentaje:
      cobradoTotal > 0 ? Math.round((n.cobrado / cobradoTotal) * 100) : 0,
  }));
  // Que las partes sumen 100 aunque redondeen.
  if (cobradoTotal > 0 && partes.length > 0) {
    const suma = partes.reduce((s, p) => s + p.porcentaje, 0);
    partes[0].porcentaje += 100 - suma;
  }
  return {
    hay,
    necesita,
    diferencia: hay - necesita,
    partes,
    esperando: nominas.filter(n => n.cobrado <= 0).map(n => n.payee),
  };
}
